// Utilità per i calcoli delle statistiche

/**
 * Calcola i punti di ranking per un giocatore
 * Formula: Mete * 5 + Pieni * 3 - Vuoti + Assist * 4 - Gialli * 10 - Rossi * 20
 */
export const calcolaPuntiRanking = (giocatore) => {
  if (!giocatore) return 0;
  
  const mete = giocatore.mete || 0;
  const pieni = giocatore.pieni || 0;
  const vuoti = giocatore.vuoti || 0;
  const assist = giocatore.assist || 0;
  const gialli = giocatore.gialli || 0;
  const rossi = giocatore.rossi || 0;
  const neri = giocatore.neri || 0;
  
  // Se ha cartellini neri, punti minimi
  if (neri >= 1) {
    return Number.MIN_SAFE_INTEGER;
  }
  
  return mete * 5 + pieni * 3 - vuoti + assist * 4 - gialli * 10 - rossi * 20;
};

/**
 * Calcola i punti di una squadra in una partita
 */
export const calcolaPuntiPartita = (mete, pieni, vuoti, assist) => {
  return (mete || 0) * 5 + (pieni || 0) * 3 - (vuoti || 0) + (assist || 0) * 4;
};

/**
 * Determina il vincitore di una partita
 * @param {Object} partita - Dati della partita
 * @returns {number} - 0 per pareggio, 1 per squadra1, 2 per squadra2
 */
export const determinaVincitore = (partita) => {
  const puntiSquadra1 = calcolaPuntiPartita(partita.mete1, partita.pieni1, partita.vuoti1, partita.assist1);
  const puntiSquadra2 = calcolaPuntiPartita(partita.mete2, partita.pieni2, partita.vuoti2, partita.assist2);
  
  if (puntiSquadra1 > puntiSquadra2) return 1;
  if (puntiSquadra2 > puntiSquadra1) return 2;
  return 0;
};

/**
 * Trova la squadra di appartenenza di un giocatore
 */
export const trovaSquadraGiocatore = (giocatore, squadre) => {
  if (!giocatore || !squadre) return null;
  
  // Prima prova a trovare tramite associazione diretta
  const squadra = squadre.find(s => 
    s.giocatori && s.giocatori.some(sg => 
      sg.nome === giocatore.nome && 
      sg.cognome === giocatore.cognome
    )
  );
  
  if (squadra) {
    return squadra;
  }
  
  // Fallback: cerca per nome maglia se disponibile
  if (giocatore.maglia) {
    return squadre.find(s => s.nome === giocatore.maglia);
  }
  
  return null;
};

/**
 * Formatta la data in formato italiano
 */
export const formattaData = (data) => {
  if (!data) return 'Data N/A';
  
  try {
    return new Date(data).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return 'Data N/A';
  }
};

/**
 * Filtra i giocatori per genere
 */
export const filtraPerGenere = (giocatori, genere) => {
  if (!giocatori) return [];
  
  return giocatori.filter(g => {
    const sesso = (g.sesso || '').toLowerCase();
    const target = genere.toLowerCase();
    
    return sesso === target || 
           (target === 'm' && ['maschio', 'male'].includes(sesso)) ||
           (target === 'f' && ['femmina', 'female'].includes(sesso));
  });
};

/**
 * Ordina i giocatori per una statistica specifica
 */
export const ordinaPerStatistica = (giocatori, statistica, decrescente = true) => {
  if (!giocatori) return [];
  
  return [...giocatori].sort((a, b) => {
    const valoreA = a[statistica] || 0;
    const valoreB = b[statistica] || 0;
    
    return decrescente ? valoreB - valoreA : valoreA - valoreB;
  });
};

/**
 * Calcola le statistiche totali di una squadra
 */
export const calcolaStatisticheSquadra = (squadra, giocatori, partite) => {
  if (!squadra) return null;
  
  // Trova giocatori della squadra
  const giocatoriSquadra = giocatori.filter(g => {
    const squadraGiocatore = trovaSquadraGiocatore(g, [squadra]);
    return squadraGiocatore && squadraGiocatore.nome === squadra.nome;
  });
  
  // Trova partite della squadra
  const partiteSquadra = partite.filter(p => 
    p.squdra1 === squadra.nome || p.squdra2 === squadra.nome
  );
  
  // Calcola totali dai giocatori
  const meteTotali = giocatoriSquadra.reduce((sum, g) => sum + (g.mete || 0), 0);
  const pieniTotali = giocatoriSquadra.reduce((sum, g) => sum + (g.pieni || 0), 0);
  const vuotiTotali = giocatoriSquadra.reduce((sum, g) => sum + (g.vuoti || 0), 0);
  const assistTotali = giocatoriSquadra.reduce((sum, g) => sum + (g.assist || 0), 0);
  
  // Calcola falli e fair play dalle partite. I falli sono un contatore e si sommano; il fair play
  // e' un voto, e la partita che non lo porta non vale zero: resta fuori dalla somma e soprattutto
  // dal denominatore della media, altrimenti una squadra verrebbe punita per un referto mancante.
  let falliTotali = 0;
  const votiFairPlay = [];
  partiteSquadra.forEach(partita => {
    const inPrimaColonna = partita.squdra1 === squadra.nome;

    falliTotali += (inPrimaColonna ? partita.falli1 : partita.falli2) || 0;

    const voto = inPrimaColonna ? partita.fairPlay1 : partita.fairPlay2;
    if (voto !== null && voto !== undefined) votiFairPlay.push(voto);
  });

  const fairPlayTotale = votiFairPlay.reduce((somma, voto) => somma + voto, 0);
  const fairPlayMedia = votiFairPlay.length
    ? Math.round((fairPlayTotale / votiFairPlay.length) * 10) / 10
    : null;
  
  // Calcola punti totali
  const puntiTotali = calcolaPuntiPartita(meteTotali, pieniTotali, vuotiTotali, assistTotali);
  
  return {
    ...squadra,
    numeroGiocatori: giocatoriSquadra.length,
    partiteTotali: partiteSquadra.length,
    meteTotali,
    pieniTotali,
    vuotiTotali,
    assistTotali,
    falliTotali,
    fairPlayTotale,
    fairPlayMedia,
    puntiTotali
  };
};

/**
 * Ottiene i migliori giocatori per una statistica specifica
 */
export const getMiglioriGiocatori = (giocatori, statistica, genere = null, limite = 5) => {
  if (!giocatori) return [];
  
  let giocatoriFiltrati = giocatori;
  
  // Filtra per genere se specificato
  if (genere) {
    giocatoriFiltrati = filtraPerGenere(giocatori, genere);
  }
  
  // Ordina per statistica e prende i primi
  return ordinaPerStatistica(giocatoriFiltrati, statistica, true).slice(0, limite);
};

/**
 * Valida i dati delle statistiche
 */
export const validaStatistiche = (statistiche) => {
  if (!statistiche) {
    return { valido: false, errore: 'Dati statistiche mancanti' };
  }
  
  if (!statistiche.giocatori || !Array.isArray(statistiche.giocatori)) {
    return { valido: false, errore: 'Dati giocatori mancanti o non validi' };
  }
  
  if (!statistiche.squadre || !Array.isArray(statistiche.squadre)) {
    return { valido: false, errore: 'Dati squadre mancanti o non validi' };
  }
  
  if (!statistiche.partite || !Array.isArray(statistiche.partite)) {
    return { valido: false, errore: 'Dati partite mancanti o non validi' };
  }
  
  return { valido: true };
};

// Costanti per le statistiche
export const STATISTICHE_CONFIG = {
  PUNTI_META: 5,
  PUNTI_PIENO: 3,
  PUNTI_VUOTO: -1,
  PUNTI_ASSIST: 4,
  PUNTI_GIALLO: -10,
  PUNTI_ROSSO: -20,
  
  COLORI_POSIZIONE: {
    1: 'first',
    2: 'second', 
    3: 'third',
    default: 'other'
  },
  
  GENERI: {
    MASCHIO: ['M', 'm', 'maschio', 'MASCHIO', 'male', 'MALE'],
    FEMMINA: ['F', 'f', 'femmina', 'FEMMINA', 'female', 'FEMALE']
  }
};